import type { SenderBatchItem } from "@/api/custom_types";
import ImportWarningDialog from "@/components/CSV/ImportWarningDialog";
import {
	useCsvExport,
	useCsvImport,
	useDisclosure,
	useTranslation,
} from "@/hooks";
import {
	useCreateBatchSenders,
	useGetAllGroups,
	useGetAllProxies,
} from "@/services";
import { addSenderTabAtom } from "@/store/sender";
import { applyDisabledFlagsForRow } from "@/utils/csv-helpers";
import {
	Button,
	Icon,
	TabItem,
	Tabs,
	TabsList,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	toast,
} from "@st-marketplace/ui";
import { useAtom } from "jotai";
import { type FC, useCallback, useEffect, useState } from "react";
import CSVImportFileDialog from "./CSVSender/ImportFileDialog";
import NewCSVSender, {
	AUTH_COLS,
	columnsKey,
	type RowData,
} from "./CSVSender/Index";
import {
	createEmptyRow,
	createSenderBatchItems,
	validateData,
} from "./CSVSender/helper";
import NewFormSender from "./FormSender/Index";
import HelpCenterDialog from "./HelpCenterDialog";

const NewSender: FC = () => {
	const { t } = useTranslation();

	const { data: senderGroups = [] } = useGetAllGroups({});
	const { data: senderProxies = [] } = useGetAllProxies({});

	const [tabValue, setTabValue] = useAtom(addSenderTabAtom);
	const [submitError, setSubmitError] = useState<string>("");

	const helpCenter = useDisclosure();

	const initialRows: RowData[] = Array.from({ length: 200 }, () =>
		createEmptyRow(),
	);

	const {
		data,
		openPipeline,
		continuePipeline,
		warning,
		fileDlg,
		setData,
		clearData,
	} = useCsvImport<RowData>(initialRows);

	const excelExport = useCsvExport<RowData>(data, columnsKey);

	const createBatchSenderMutation = useCreateBatchSenders();

	const [isDataValid, setIsDataValid] = useState<boolean>(true);

	const handleImportFile = useCallback(
		(rows: RowData[]) => {
			for (const row of rows) {
				applyDisabledFlagsForRow<RowData, typeof AUTH_COLS>(row, AUTH_COLS);
			}
			setData(rows);
			fileDlg.onClose();
		},
		[fileDlg.onClose, setData],
	);

	useEffect(() => {
		const { isValid, errorMessage } = validateData(data, t);
		setIsDataValid(isValid);
		if (!isValid) {
			setSubmitError(errorMessage);
		} else {
			setSubmitError("");
		}
	}, [data, t]);

	const submit = useCallback(
		(rows: RowData[]) => {
			const batchItems: SenderBatchItem[] = createSenderBatchItems(
				rows,
				senderGroups,
				senderProxies,
			);

			createBatchSenderMutation.mutate(
				{ body: { senders: batchItems } },
				{
					onSuccess: () => {
						toast.success(t("addSender.csv.messagesSuccessfulCreation"));
						clearData();
					},
					onError: (err) => toast.error((err as Error).message),
				},
			);
		},
		[senderGroups, senderProxies, createBatchSenderMutation, t, clearData],
	);

	const handleTabChange = (value: string) =>
		setTabValue(value === "csv" ? 1 : 0);

	return (
		<>
			<div className="mb-5">
				<Tabs
					value={tabValue === 1 ? "csv" : "form"}
					onValueChange={handleTabChange}
				>
					<TabsList>
						<TabItem value="form" Icon={<Icon name="user-add-line" />}>
							{t("addSender.tabForm")}
						</TabItem>
						<TabItem value="csv" Icon={<Icon name="file-excel-line" />}>
							{t("addSender.tabCsv")}
						</TabItem>
					</TabsList>
				</Tabs>

				{tabValue === 1 ? (
					<div className="flex flex-wrap justify-end items-center gap-4 mt-4">
						<Button
							type="button"
							icon
							color="ghost"
							size="sm"
							aria-label={t("addSender.csv.helpTitle")}
							onClick={helpCenter.onOpen}
						>
							<Icon name="question-line" className="text-primary-100" />
						</Button>

						<Button
							type="button"
							color="secondary"
							size="sm"
							className="w-[80%] normal-case md:w-auto"
							onClick={() => {
								const link = document.createElement("a");
								link.href = "/assets/files/senders_data.csv";
								link.download = "senders_data.csv";
								document.body.appendChild(link);
								link.click();
								document.body.removeChild(link);
							}}
						>
							{t("addSender.csv.buttonDownloadTemplate")}
						</Button>

						<Button
							type="button"
							color="secondary"
							size="sm"
							className="normal-case"
							onClick={openPipeline}
						>
							{t("addSender.csv.buttonImportFile")}
						</Button>

						<Button
							type="button"
							color="secondary"
							size="sm"
							className="normal-case"
							onClick={() => excelExport("senders_data")}
						>
							{t("addSender.csv.buttonExportFile")}
						</Button>

						<Tooltip>
							<TooltipTrigger asChild>
								<span className="inline-flex">
									<Button
										type="button"
										color="primary"
										size="sm"
										className="px-10 normal-case"
										loading={createBatchSenderMutation.isPending}
										disabled={!isDataValid}
										onClick={() => submit(data)}
									>
										{t("addSender.csv.buttonSubmit")}
									</Button>
								</span>
							</TooltipTrigger>
							{!!submitError && (
								<TooltipContent>
									<p>{submitError}</p>
								</TooltipContent>
							)}
						</Tooltip>
					</div>
				) : (
					<div />
				)}
			</div>

			{tabValue === 0 ? (
				<NewFormSender />
			) : (
				<NewCSVSender
					data={data}
					onChange={setData}
					groups={senderGroups}
					proxies={senderProxies}
				/>
			)}

			<HelpCenterDialog open={helpCenter.open} onClose={helpCenter.onClose} />

			<CSVImportFileDialog
				open={fileDlg.open}
				onClose={fileDlg.onClose}
				onImport={handleImportFile}
			/>

			<ImportWarningDialog
				open={warning.open}
				onClose={warning.onClose}
				onContinue={continuePipeline}
			/>
		</>
	);
};

export default NewSender;
